import {
  Box,
  Divider,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import {
  CardSection,
  CardSubSection,
  TextField,
  useIsMobile,
} from '@side/pantry';
import { useMemo, useRef, useState } from 'react';
import { formatPrice } from '@/common/utils/money';
import { usePayment } from '$api-clients/hooks';
import { useCompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { ExportToPNGButton } from '../components/ExportToPNGButton';
import { FilterValuesFooter } from '../FilterValuesFooter/FilterValuesFooter';
import { buildReportingSummaryQuery, useViewReportAs } from '../helpers';
import { NoResultsOverlay } from '../NoResultsOverlay/NoResultsOverlay';
import { useAllTimeStartDate } from '../ReportsConfiguration/useReportsConfiguration';
import {
  useReportsRole,
  useRoleCanSee,
} from '../ReportsConfiguration/useReportsRole';
import {
  mapTransactionTypeBreakdownData,
  TRANSACTION_TYPE_CHART_COLORS,
  TRANSACTION_TYPE_LABELS,
  type TransactionTypeId,
} from './TransactionTypeBreakdown.helpers';
import { TransactionTypeBreakdownCenterLabel } from './TransactionTypeBreakdownCenterLabel';
import { TransactionTypeBreakdownTooltip } from './TransactionTypeBreakdownTooltip';

const CHART_SIZE_DESKTOP = 240;
const CHART_SIZE_MOBILE = 130;
const INNER_RADIUS_DESKTOP = 82;
const INNER_RADIUS_MOBILE = 46;
const PIE_THICKNESS_DESKTOP = 32;
const PIE_THICKNESS_MOBILE = 20;
const PADDING_ANGLE = 0.6;
const IMAGE_NAME = 'transaction-type-breakdown.png';

function TransactionTypeDonutSkeleton({ isMobile }: { isMobile: boolean }) {
  const size = isMobile ? CHART_SIZE_MOBILE : CHART_SIZE_DESKTOP;
  const innerR = isMobile ? INNER_RADIUS_MOBILE : INNER_RADIUS_DESKTOP;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        ml: { xs: 0, md: 3 },
        mr: { xs: 0, md: 5 },
      }}
    >
      <Skeleton
        variant="circular"
        animation="wave"
        sx={{ width: size, height: size }}
      />
      <Box
        sx={(theme) => ({
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: innerR * 2,
          height: innerR * 2,
          borderRadius: '50%',
          bgcolor: theme.palette.background.paper,
        })}
      />
    </Box>
  );
}

type LegendItem = {
  id: string;
  label: string;
  color?: string;
  value?: number | null;
};

export function TransactionTypeBreakdown() {
  const muiTheme = useTheme();
  const fontFamily = muiTheme.typography.fontFamily;
  const isMobile = useIsMobile('md');
  const [showNet, setShowNet] = useState<
    'agent_net' | 'team_net' | 'brand_net'
  >('agent_net');
  const chartRef = useRef<HTMLDivElement>(null);

  const { filters } = useCompensationReportFilters();
  const viewReportAs = useViewReportAs();
  const allTimeStartDate = useAllTimeStartDate();
  const query = viewReportAs
    ? buildReportingSummaryQuery(filters, viewReportAs, allTimeStartDate)
    : null;
  const { data: transactionTypeBreakdownData, isLoading } = usePayment(
    '/reporting/summary',
    query
      ? {
          params: {
            query: {
              ...query,
              limit: 10000,
              offset: 0,
              groupBy: ['TRANSACTION_TYPE'],
            },
          },
        }
      : null,
  );

  const chartData = useMemo(
    () =>
      mapTransactionTypeBreakdownData(
        transactionTypeBreakdownData?.results ?? [],
        showNet,
      ),
    [transactionTypeBreakdownData?.results, showNet],
  );
  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + (item.value ?? 0), 0),
    [chartData],
  );
  const hasData = !!transactionTypeBreakdownData?.results?.length;
  const hasNetCommissions = total > 0;
  const chartSeriesData =
    hasData && hasNetCommissions
      ? chartData
      : [
          {
            id: 'empty',
            label: '',
            value: 1,
            color: muiTheme.palette.grey[300],
          },
        ];
  const legendData: LegendItem[] = hasData
    ? chartData.map((item) => {
        let label = '';
        if (typeof item.label === 'string') {
          label = item.label;
        } else if (typeof item.label === 'function') {
          label = item.label('legend');
        }
        return {
          id: String(item.id),
          label,
          color: item.color,
          value: item.value ?? null,
        };
      })
    : (Object.keys(TRANSACTION_TYPE_LABELS) as TransactionTypeId[]).map(
        (transactionType) => ({
          id: transactionType,
          label: TRANSACTION_TYPE_LABELS[transactionType],
          color: TRANSACTION_TYPE_CHART_COLORS[transactionType],
          value: null,
        }),
      );
  const { isAgent } = useReportsRole();
  const { roleCanSee } = useRoleCanSee();

  const groupBySelector = !isAgent && (
    <TextField
      select
      value={showNet}
      disabled={isLoading}
      sx={{ width: 160 }}
      onChange={(e) =>
        setShowNet(e.target.value as 'agent_net' | 'team_net' | 'brand_net')
      }
      size="small"
      SelectProps={{
        renderValue: (value) => (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, pr: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Show
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {(() => {
                switch (value) {
                  case 'agent_net':
                    return 'Agent Net';
                  case 'team_net':
                    return 'Team Net';
                  case 'brand_net':
                    return 'Brand Net';
                  default:
                    return null;
                }
              })()}
            </Typography>
          </Stack>
        ),
      }}
    >
      <MenuItem value="agent_net">Agent Net</MenuItem>
      {roleCanSee('brand') && <MenuItem value="brand_net">Brand Net</MenuItem>}
      {roleCanSee('team') && <MenuItem value="team_net">Team Net</MenuItem>}
    </TextField>
  );

  const legend = isLoading ? (
    <Stack
      component="ul"
      sx={{
        fontFamily,
        listStyle: 'none',
        m: 0,
        p: 0,
        gap: 2,
        minWidth: 200,
      }}
    >
      <Skeleton variant="rectangular" width="100%" height={20} />
      <Skeleton variant="rectangular" width="100%" height={20} />
      <Skeleton variant="rectangular" width="100%" height={20} />
    </Stack>
  ) : (
    <Stack
      component="ul"
      sx={{
        fontFamily,
        listStyle: 'none',
        m: 0,
        p: 0,
        gap: { xs: 1.25, md: 2 },
        minWidth: 180,
      }}
    >
      {legendData.map((item) => {
        let percentage: string | null = null;

        if (typeof item.value === 'number') {
          if (hasNetCommissions) {
            percentage = parseFloat(
              (((item.value ?? 0) / total) * 100).toPrecision(2),
            ).toString();
          } else {
            percentage = '0';
          }
        }

        return (
          <Stack
            component="li"
            key={item.id}
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color ?? 'grey.500',
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2">{item.label}</Typography>
            </Stack>
            <Typography variant="body2">
              {percentage !== null ? `${percentage}%` : '––%'}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <CardSection
      id="transaction-type-breakdown-card-section"
      cardTitle="Transaction Type Breakdown"
      action={
        <ExportToPNGButton
          disabled={!hasData || isLoading}
          chartElement={chartRef.current}
          imageName={IMAGE_NAME}
        />
      }
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .MuiCollapse-root, & .MuiCollapse-wrapper': { height: '100%' },
      }}
    >
      <CardSubSection>
        <Stack
          sx={{
            gap: { xs: 3, md: 8 },
            justifyContent: 'space-between',
            overflow: 'auto',
          }}
          ref={chartRef}
        >
          <NoResultsOverlay
            isActive={!hasData && !isLoading}
            message="No earnings match the current filters"
          >
            <Stack sx={{ gap: { xs: 3, md: 8 } }}>
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  alignItems: 'center',
                  py: 0.5,
                  justifyContent: { xs: 'space-between', md: 'flex-start' },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary' }}
                >
                  Net Commission
                </Typography>
                {groupBySelector}
              </Stack>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {isLoading ? (
                  <>
                    <TransactionTypeDonutSkeleton isMobile={isMobile} />
                    {legend}
                  </>
                ) : (
                  <>
                    <MuiPieChart
                      series={[
                        {
                          data: chartSeriesData,
                          innerRadius: isMobile
                            ? INNER_RADIUS_MOBILE
                            : INNER_RADIUS_DESKTOP,
                          outerRadius: isMobile
                            ? INNER_RADIUS_MOBILE + PIE_THICKNESS_MOBILE
                            : INNER_RADIUS_DESKTOP + PIE_THICKNESS_DESKTOP,
                          paddingAngle: PADDING_ANGLE,
                          highlightScope: hasNetCommissions
                            ? { fade: 'global', highlight: 'item' }
                            : { fade: 'none', highlight: 'none' },
                          arcLabel: () => '',
                        },
                      ]}
                      sx={{
                        justifyContent: 'space-evenly',
                        alignSelf: 'flex-start',
                      }}
                      width={isMobile ? CHART_SIZE_MOBILE : CHART_SIZE_DESKTOP}
                      height={isMobile ? CHART_SIZE_MOBILE : CHART_SIZE_DESKTOP}
                      hideLegend={isMobile}
                      slots={{
                        tooltip: hasNetCommissions
                          ? TransactionTypeBreakdownTooltip
                          : () => null,
                        legend: () => legend,
                      }}
                    >
                      <TransactionTypeBreakdownCenterLabel
                        innerRadius={
                          isMobile ? INNER_RADIUS_MOBILE : INNER_RADIUS_DESKTOP
                        }
                      >
                        {hasData ? formatPrice(total, true, false) : '––'}
                      </TransactionTypeBreakdownCenterLabel>
                    </MuiPieChart>
                    {isMobile && legend}
                  </>
                )}
              </Stack>
            </Stack>
          </NoResultsOverlay>
          <FilterValuesFooter variant="compensation" />
        </Stack>
      </CardSubSection>
    </CardSection>
  );
}
